import React, { useState, useEffect, useCallback } from 'react';
import { Button, ComboBox, Table } from '@ds/ui'
import { ProcessSpinner } from '@ds/icons';
import ExportWorkflow from '../importExport/ExportWorkflow';
import ExportInfo from '../../types/ExportInfo';
import ImportWorkflow from '../importExport/ImportWorkflow';
import ImportDPUKey from './ImportDPUKey';
import { BannerInfo } from '../../types/BannerInfo';
import { useConfig } from '../../context/ConfigContext';
import NumKeyModal from './NumKeyModal';
import { apiFetch, handleApiError, handleApiErrorWithBanner } from '../../utils/apiClient';
import { ImportInfo } from '../../types/ImportInfo';

interface NumKeysProps {
  onPendingKeysChange: (pendingKeys: string) => void
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void
  exportInfo?: ExportInfo
}

export default function NumKeys({ 
  onPendingKeysChange, 
  onBannerUpdate,
  exportInfo: propExportInfo,
}: NumKeysProps) {
    const [activeKeys, setActiveKeys] = useState(0)
    const [pendingKeys, setPendingKeys] = useState(0)
    const [pendingKeysChecksum, setPendingKeysChecksum] = useState('')
    const [activeKeysChecksum, setActiveKeysChecksum] = useState('')
    const [isExporting, setIsExporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [selectedCreationOption, setSelectedCreationOption] = useState('')
    const [selectedExportOption, setSelectedExportOption] = useState('')
    const [localExportInfo, setLocalExportInfo] = useState<ExportInfo>();
    const [localImportInfo, setLocalImportInfo] = useState<ImportInfo>();
    const [showExportWorkflow, setShowExportWorkflow] = useState(false)
    const [showImportWorkflow, setShowImportWorkflow] = useState(false)
    const { internalSiteEnabled } = useConfig();
    const [isCreating, setIsCreating] = useState(false);
    const [isCreateLoading, setIsCreateLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [deleteComplete, setDeleteComplete] = useState(false);

    // Watch for changes in propExportInfo and fetch if active
    useEffect(() => {
      if (propExportInfo?.inProgress === true && propExportInfo?.filehash !== null) {
        fetchData()
      }
    }, [propExportInfo]);

    const fetchData = useCallback(async () => {
      try {
        
        const response = await fetch('Admin/Home/GetNumKeysInfo', {
          method: 'GET'
        })
        
        if (!response.ok) {
          await handleApiError(response);
        }
        
        const data = await response.json()
        setActiveKeys(data.activeKeys.toUpperCase())
        setPendingKeys(data.pendingKeys.toUpperCase())
        onPendingKeysChange(data.pendingKeys)
        setPendingKeysChecksum(data.pendingKeysChecksum)
        setActiveKeysChecksum(data.activeKeysChecksum)
        handleGetExportInfo();
        handleGetImportInfo();
      } catch (error) {
        console.error('Error fetching keys data:', error)
        onBannerUpdate({ 
          type: 'danger', 
          message: 'Failed to fetch keys data. Please try again.', 
          retryAction: fetchData 
        })
      }
    }, [onPendingKeysChange])
  
    useEffect(() => {
      fetchData()
    }, [fetchData])
  
    useEffect(() => {
      if (localExportInfo?.inProgress === true && localExportInfo?.filehash !== null) {
        setShowExportWorkflow(true)
      }
      if (localImportInfo?.inProgress === true && localImportInfo?.redKeyUploaded === true) {
        setShowImportWorkflow(true)
      } 
    }, [localExportInfo, localImportInfo])

    const internalKeyCreationOptions = [
      { text: '1K' },
    ]
  
    const commonKeyCreationOptions = [
      { text: '10K' },
      { text: '100K' },
      { text: '1M' },
      { text: '10M' }
    ]

    const buildKeyCreationOptions = () => {
      if (internalSiteEnabled) {
        return internalKeyCreationOptions.concat(commonKeyCreationOptions);
      }
      return commonKeyCreationOptions;
    }

    const keyExportOptions = [
        { text: "All" },
        { text: "Pending" }
      ]
  
    const handleCreationOptionSelect = (text: string) => {
      setSelectedCreationOption(text)
    }
  
    const handleExportOptionSelect = (text: string) => {
      setSelectedExportOption(text)
    }
  
    const handleCreateKeys = async () => {
      if (!selectedCreationOption) {
        onBannerUpdate({ type: 'danger', message: 'Please select a number of keys to create' });
        return;
      }
      
      
      try {
        let callTimes = 1;
        if (selectedCreationOption.includes("M")) {
          setIsCreating(true);
          callTimes = parseInt(selectedCreationOption.replace("M", ""));
          callTimes = callTimes * 10;
        
          for (let i = 0; i < callTimes; i++) {
            const response = await apiFetch('Admin/Home/Create', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                numKeys: "100000"  // send 100,000 keys each time
              })
            });
            
            if (!response.ok) {
              await handleApiError(response);
            }

            setProgress(Math.round((i / callTimes) * 100));
          }
        } else {
          setIsCreateLoading(true);
          const response = await apiFetch('Admin/Home/Create', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                numKeys: selectedCreationOption 
              })
            });
            if (!response.ok) {
              await handleApiError(response);
            }
        }
        
        fetchData();
      } catch (error) {
        console.error('Error creating keys:', error);
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to create keys. Please try again.';
        
        onBannerUpdate({ 
          type: 'danger', 
          message: errorMessage, 
          retryAction: handleCreateKeys 
        });
      } finally {
        setIsCreateLoading(false);
        setIsCreating(false);
        setProgress(0);
      }
    };
  
    const handleDelete = async (retryCount = 0): Promise<void> => {
      try {
        setIsDeleteLoading(true);
        const response = await apiFetch('Admin/Home/Delete', {
          method: 'DELETE'
        });
        
        const data = await response.json();
        
        if (!response.ok || (data !== "There are no keys to delete" && data.error)) {
          // Special case for timeout error
          if (data.error?.includes("HttpClient.Timeout")) {
            if (retryCount < 10) {
              // Don't show error banner for timeout, just retry 
              await new Promise(resolve => setTimeout(resolve, 2000));
              return handleDelete(retryCount + 1);
            }
          } else if (data.error) {
            // Show banner for non-timeout errors
            throw new Error(data.error);
          }
          
          throw new Error(data.error || 'Failed to delete key. Please try again.');
        }
        
        setDeleteComplete(true);
        setTimeout(() => {
          setDeleteComplete(false);
        }, 20000);
        
        fetchData();
        onBannerUpdate({ 
          type: 'success', 
          message: 'Pending keys deleted successfully'
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete key';
        onBannerUpdate({ 
          type: 'danger', 
          message: errorMessage, 
          retryAction: () => handleDelete(0)
        });
      } finally {
        setIsDeleteLoading(false);
      }
    };
  
    const handleExportClick = () => {
      handleExport();
    };

    const handleImportClick = () => {
      setShowImportWorkflow(true)
    };

    const handleCancel = () => {
      // Implement cancel logic here
      setShowExportWorkflow(false)
      setIsExporting(false);
      setProgress(0);
    };

    const pollExportInfo = async (maxAttempts = 100000): Promise<ExportInfo | null> => {
      for (let i = 0; i < maxAttempts; i++) {
        const response = await apiFetch('Admin/Home/GetExportInfo');
        
        if (!response.ok) {
            const errorData = await response.json();
            if (errorData.error && errorData.error.includes("Export already in progress")) {
              continue;
            } else {
              const errorMessage = await handleApiErrorWithBanner(response);
              throw new Error(errorMessage);
            }
        }
        
        const data = await response.json();
        const info = data.exportInfo; 
        if (info && info.filehash) {
          return info;
        }
        setProgress(info.percentComplete);
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1s between attempts
      }
      return null;
    };

    const handleExport = useCallback(async () => {
      if (!selectedExportOption) {
        onBannerUpdate({ type: 'danger', message: 'Please select a number of keys to export' });
        return;
      }
      setIsExporting(true);
      setProgress(0);
      try {
        const formData = new FormData()
        formData.append('numkeys', selectedExportOption)
        
        const response = await apiFetch('Admin/Home/Export', {
          method: 'POST',
          body: formData
        })
        
        if (!response.ok) { 
          const errorMessage = await handleApiErrorWithBanner(response);
          onBannerUpdate({ 
            type: 'danger',
            message: `Failed to export keys: ${errorMessage}`,
            retryAction: handleExport 
          });
          throw new Error(`Failed to export keys: ${errorMessage}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        if (!reader) {
          throw new Error('Failed to get response reader');
        }

        const decoder = new TextDecoder()
        let buffer = ''
  
        while (true) {
          const { done, value } = await reader.read()
          if (done) break;
          
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          try {
            const data = JSON.parse(line)
            if (data.progress !== undefined) {
              setProgress(data.progress)
            }
          } catch (e) {
            console.error('Error parsing JSON:', e)
          }
        }
      }
  
      const exportInfo = await pollExportInfo();
      if (exportInfo) {
        setLocalExportInfo(exportInfo);
      }
      setShowExportWorkflow(true)

      } catch (error) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to export keys. Please try again.';
        
        onBannerUpdate({ 
          type: 'danger', 
          message: errorMessage, 
          retryAction: handleExport 
        })
        throw error instanceof Error ? error : new Error('Failed to export keys');
      } finally {
        setIsExporting(false);
        setProgress(100);
      }
    }, [selectedExportOption]);
  
    const handleGetExportInfo = async () => {
      const response = await fetch('Admin/Home/GetExportInfo')
      if (!response.ok) {
        const errorMessage = await handleApiErrorWithBanner(response);
        onBannerUpdate({ 
          type: 'danger', 
          message: 'Failed to fetch export info: ' + errorMessage, 
          retryAction: handleGetExportInfo 
        })
        throw new Error(`Failed to fetch export info: ${errorMessage}`);
      }

      const data = await response.json();
      const exportInfo = data.exportInfo;
      setLocalExportInfo(exportInfo);
    }

    const handleGetImportInfo = async () => {
      const response = await fetch('Admin/Home/GetImportInfo')
      if (!response.ok) {
        const errorMessage = await handleApiErrorWithBanner(response);
        onBannerUpdate({ 
          type: 'danger', 
          message: 'Failed to fetch import info: ' + errorMessage, 
          retryAction: handleGetImportInfo 
        })
        throw new Error('Failed to fetch import info: ' + errorMessage);
      }

      const data = await response.json();
      const importInfo = data.importInfo;
      setLocalImportInfo(importInfo);
    }

    const handleImportComplete = useCallback(() => {
      setShowImportWorkflow(false)
      fetchData() 
    }, [fetchData])
    
  return (
    <div>
      <div>
        <Table columnSettings={[
          { alignment: 'left', width: '10rem' },
          { alignment: 'left', width: '3.125rem' },
          { alignment: 'left', width: '12.5rem' },
          { alignment: 'left', width: '12.5rem' },
          { alignment: 'left', width: '12.5rem' },
        ]}>
          <Table.Body>
            <Table.Row>
              <Table.Cell _style={{ width: '10rem', paddingTop: 0 }}>Active Keys</Table.Cell>
              <Table.Cell _style={{ paddingTop: 0 }}>{activeKeys}</Table.Cell>
              {activeKeysChecksum && activeKeysChecksum !== "" ? (
                <Table.Cell _style={{ paddingTop: 0 }}>(Checksum: {activeKeysChecksum})</Table.Cell>
              ) : null}
            </Table.Row>
            <Table.Row>
              <Table.Cell _style={{ width: '10rem', paddingTop: 0 }}>Pending Keys</Table.Cell>
              <Table.Cell _style={{ paddingTop: 0 }}>{pendingKeys}</Table.Cell>
              {pendingKeys === 0 && (
                <>
                  <Table.Cell _style={{ paddingTop: 0 }}></Table.Cell>
                  <Table.Cell _style={{ paddingTop: 0 }}></Table.Cell>
                </>
              )}
             
              {pendingKeysChecksum && pendingKeysChecksum !== "" ? (
                <Table.Cell _style={{ paddingTop: 0 }}>(Checksum: {pendingKeysChecksum})</Table.Cell>
              ) : null}
            </Table.Row>
          </Table.Body>
        </Table>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem', paddingLeft: '1rem'}}>
            <ComboBox
              width="17.5rem"
              label=""
              placeholder="Select # of new keys"
              value={selectedCreationOption}
            >
              {buildKeyCreationOptions().map((option) => (
                <ComboBox.Option
                  key={option.text}
                  text={option.text}
                  onSelect={() => handleCreationOptionSelect(option.text)}
                  selected={selectedCreationOption === option.text}
                />
              ))}
            </ComboBox>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button 
                text={isCreating ? "Creating..." : "Create"} 
                kind="secondary" 
                onClick={handleCreateKeys} 
                disabled={isCreating || isCreateLoading}
              />
              {isCreateLoading && (
                <div style={{ 
                  position: 'absolute', 
                  top: '50%',
                  right: '50%',
                  transform: 'translate(50%, -50%)',
              }}>
                <ProcessSpinner />
              </div>
              )}
              <NumKeyModal
                mode="create"
                isOpen={isCreating}
                progress={progress}
                onCancel={() => setIsCreating(false)}
                onComplete={() => setIsCreating(false)}
              />
            </div>
          </div>

          {pendingKeys !== 0 && (
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '2.3125rem', paddingLeft: '1rem' }}> 
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Button text="Delete Pending Keys" kind="secondary" onClick={() => handleDelete(0)} disabled={isDeleteLoading || pendingKeys == 0} />
                {isDeleteLoading && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%',
                    right: '50%',
                    transform: 'translate(50%, -50%)',
                  }}>
                    <ProcessSpinner />
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '2.3125rem', paddingLeft: '1rem' }}>
            <ComboBox
              width="17.5rem"
              label=""
              placeholder="Select # of new keys to export"
              value={selectedExportOption}
            >
              {keyExportOptions.map((option) => (
                <ComboBox.Option
                  key={option.text}
                  text={option.text}
                  onSelect={() => handleExportOptionSelect(option.text)}
                  selected={selectedExportOption === option.text}
                />
              ))}
            </ComboBox>
            <Button text="Export" kind= "secondary" onClick={handleExportClick} disabled={isExporting || showExportWorkflow} />
            <NumKeyModal
              mode="export"
              isOpen={isExporting}
              progress={progress}
              onCancel={handleCancel}
              onComplete={handleCancel}
            />
          </div>
          {showExportWorkflow && (
            <div style={{paddingLeft: '1rem', paddingTop: '1rem' }}> 
              <ExportWorkflow
                mode="export"
                controller="Home"
                onBannerUpdate={onBannerUpdate}
                onComplete={() => {
                  setShowExportWorkflow(false)
                }}
              />
            </div>
          )} 
          
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '2.3125rem', paddingLeft: '1rem' }}> 
            <Button
              text="Import Keys from File"
              kind="secondary"
              disabled={showImportWorkflow}
              onClick={handleImportClick}
            />
          </div>
          
          {showImportWorkflow && (
            <div style={{paddingLeft: '1rem', paddingTop: '2.3125rem' }}> 
              <ImportWorkflow
                mode="namedKey"
                controller="Home"
                onComplete={handleImportComplete}
                onBannerUpdate={onBannerUpdate}
              />
            </div>
          )}
          {internalSiteEnabled && (
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '2.3125rem', paddingLeft: '1rem' }}> 
              <ImportDPUKey onBannerUpdate={onBannerUpdate}/>
            </div>
          )}
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.8125rem', paddingLeft: '1rem' }}> 
          </div>
        </div>
      </div>
    </div>
  );
};
